import { Subscribe, type RowData } from "@tanstack/react-table";
import type { ViewTableInstance, ViewTableRow } from "./core/table";
import classNames from "classnames";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { buildShiftClickSelection } from "./ViewTable.lib";
import type { ViewTableMeta } from "./ViewTable.lib";
import styles from "./ViewTable.module.scss";

function handleCheckboxClick<TData extends RowData>(
    e: React.MouseEvent<HTMLDivElement>,
    row: ViewTableRow<TData>,
    table: ViewTableInstance<TData>,
) {
    e.preventDefault();
    e.stopPropagation();

    if (!row.getCanSelect()) {
        return;
    }

    const meta = table.options.meta as
        | ViewTableMeta<unknown, unknown>
        | undefined;
    const anchorRef = meta?.lastSelectedRowIdRef;

    if (e.shiftKey) {
        const visibleRows = table.getRowModel().rows;
        const clickedIndex = visibleRows.findIndex((r) => r.id === row.id);
        if (clickedIndex === -1) {
            return;
        }

        table.options.onRowSelectionChange?.((prev) =>
            buildShiftClickSelection({
                visibleRows,
                anchorRowId: anchorRef?.current ?? null,
                clickedIndex,
                prev,
            }),
        );
    } else {
        row.toggleSelected();
    }

    if (anchorRef) {
        anchorRef.current = row.id;
    }

    // Drop focus from the checkbox label/input so subsequent single-letter
    // hotkeys (e.g. `c` in SimilarTransactionsUpdateDrawer) still fire.
    // react-hotkeys-hook ignores keypresses while focus is on form inputs.
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
}

interface ViewTableCheckboxCellProps<TData extends RowData> {
    readonly row: ViewTableRow<TData>;
    readonly table: ViewTableInstance<TData>;
    /** Passed by `ViewTableRowComponent`, which reads it fresh each render. */
    readonly canSelect?: boolean;
}

const renderRowCheckbox = <TData extends RowData>({
    row,
    table,
    canSelect = row.getCanSelect(),
}: ViewTableCheckboxCellProps<TData>) => {
    const meta = table.options.meta as
        | ViewTableMeta<unknown, unknown>
        | undefined;

    // Hide checkbox entirely when consumer requests it
    if (meta?.getRowSelectionHidden?.(row.original)) {
        return null;
    }

    const disabledTooltip = !canSelect
        ? meta?.getRowSelectionDisabledTooltip?.(row.original)
        : undefined;

    // A group/parent reads as checked when its children are, even if its own
    // row id was never ticked (children can be selected one by one), and as a
    // dash when only some are. Two subtleties:
    //  - `getIsAllSubRowsSelected()` skips non-selectable rows, so a group that
    //    is still loading (a skeleton / load-more / error row among its
    //    children) would report a premature "all". Require every sub-row to be
    //    selectable before treating it as fully checked, so an unfinished group
    //    reads as a dash rather than a check.
    //  - In that same partly-loaded state TanStack reports neither "some" nor a
    //    real "all", so drive the dash off "any selectable descendant selected"
    //    (`getIsSomeSelected() || getIsAllSubRowsSelected()`) rather than
    //    `getIsSomeSelected()` alone.
    const hasSelectableSubRow = row.subRows.some((subRow) =>
        subRow.getCanSelect(),
    );
    const isAllSubRowsSelected =
        hasSelectableSubRow &&
        row.subRows.every((subRow) => subRow.getCanSelect()) &&
        row.getIsAllSubRowsSelected();
    const isParentSelectionSelf = meta?.parentSelection === "self";
    const isChecked =
        row.getIsSelected() || (!isParentSelectionSelf && isAllSubRowsSelected);
    const hasSelectedSubRow =
        row.getIsSomeSelected() || row.getIsAllSubRowsSelected();

    const checkbox = (
        <div
            className={styles.selectCheckboxWrapper}
            onClickCapture={(e) => {
                if (e.shiftKey) {
                    handleCheckboxClick(e, row, table);
                }
            }}
            onClick={(e) => {
                if (!e.shiftKey) {
                    handleCheckboxClick(e, row, table);
                }
            }}
        >
            <Checkbox
                data-testid={`view-table-row-select-${row.id}`}
                className={classNames(
                    styles.selectCheckbox,
                    styles.rowSelect,
                    styles.cellSelectionCheckbox,
                )}
                checked={isChecked}
                indeterminate={
                    !isParentSelectionSelf &&
                    !isChecked &&
                    hasSelectableSubRow &&
                    hasSelectedSubRow
                }
                disabled={!canSelect}
            />
        </div>
    );

    if (!disabledTooltip) {
        return checkbox;
    }

    return <Tooltip content={disabledTooltip}>{checkbox}</Tooltip>;
};

/**
 * TanStack keeps `row` and `table` stable while selection changes inside
 * them, so the selection getters run in a `Subscribe` render function, which
 * React Compiler doesn't cache.
 */
export const ViewTableCheckboxCell = <TData extends RowData>({
    row,
    table,
    canSelect,
}: ViewTableCheckboxCellProps<TData>) => (
    <Subscribe source={table.atoms.rowSelection}>
        {() => renderRowCheckbox({ row, table, canSelect })}
    </Subscribe>
);

ViewTableCheckboxCell.displayName = "ViewTableCheckboxCell";
